/** Operate only controls in this app. No model-generated JavaScript or selectors. */
export interface PageAction { id: string; operation: 'snapshot'|'navigate'|'click'|'fill'|'select'|'check'|'read_table'; target: string; value: string|boolean }
const nodes = new Map<string, HTMLElement>();
const ids = new WeakMap<HTMLElement, string>();
let sequence = 0;
let snapshotRoute = '';
const blocked = /^(run scenario|run both models|confirm.*run|rebuild baseline|delete|clear all|connection settings)/i;
function visible(el: HTMLElement) {
  if (el.closest('[hidden],.research-chat,.research-dock')) return false;
  const closed = el.closest('details:not([open])');
  if (closed && el.tagName !== 'SUMMARY') return false;
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && !!el.getClientRects().length;
}
function label(el: HTMLElement) {
  return (el.getAttribute('aria-label') || (el as HTMLInputElement).labels?.[0]?.textContent || el.textContent || el.getAttribute('title') || '').trim().replace(/\s+/g,' ').slice(0,300);
}
function controls() {
  const root = document.querySelector('dialog[open]') || document.querySelector('main');
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>('button,a,input,select,textarea,summary,[role="option"]')).filter(visible);
}
export function pageSnapshot() {
  nodes.clear(); snapshotRoute = window.location.hash;
  const elements = controls().slice(0,400).map(el => {
    let id = ids.get(el); if (!id) { id = `control-${++sequence}`; ids.set(el,id); } nodes.set(id,el);
    const input = el as HTMLInputElement;
    return { id, label:label(el), type:el.getAttribute('role') || input.type || el.tagName.toLowerCase(),
      group:el.closest('[aria-label],fieldset,details')?.getAttribute('aria-label') || el.closest('details')?.querySelector('summary')?.textContent?.trim(),
      disabled:!!input.disabled, value: input.type === 'checkbox' ? input.checked : input.value,
      pressed:el.getAttribute('aria-pressed'), expanded:el.getAttribute('aria-expanded'),
      requires_user_action:blocked.test(label(el)),
      ...(el instanceof HTMLSelectElement ? {options:Array.from(el.options).map(o=>({label:o.text,value:o.value}))} : {}) };
  });
  const content = document.querySelector('dialog[open]') || document.querySelector('main');
  // innerText excludes collapsed/hidden sections; only this visitor's displayed page is shared.
  return {route:window.location.hash, controls:elements, text:((content as HTMLElement)?.innerText || '').slice(0,16000)};
}
export function performPageAction(action: PageAction): Record<string,unknown> {
  if (action.operation === 'snapshot') return pageSnapshot();
  if (action.operation === 'navigate') {
    const route = String(action.value);
    if (!/^\/#\/(overview|model|computation|calibration|simulation)(\/[a-zA-Z0-9_-]+)?$/.test(route)) throw new Error('Choose an internal research-page route.');
    window.location.hash = route.slice(1); return {navigated:route};
  }
  if (snapshotRoute !== window.location.hash) throw new Error('The page changed. Request a new snapshot.');
  if (action.operation === 'read_table') {
    const tables = Array.from(document.querySelectorAll<HTMLTableElement>('main table,dialog[open] table')).filter(visible);
    return {tables:tables.slice(0,3).map(table=>({title:table.caption?.textContent,
      rows:Array.from(table.rows).slice(0,101).map(row=>Array.from(row.cells).map(cell=>cell.textContent?.trim())),
      total_rows:table.rows.length})), note:'At most 100 data rows per table. Use the page search to narrow results.'};
  }
  const el=nodes.get(action.target);
  if (!el || !el.isConnected || !visible(el)) throw new Error('Control is stale or hidden. Request a new snapshot.');
  if ((el as HTMLInputElement).disabled) throw new Error('This control is disabled.');
  if (blocked.test(label(el))) { el.scrollIntoView({block:'center'}); return {requires_user_action:label(el)}; }
  el.scrollIntoView({block:'nearest'});
  if (action.operation === 'click') {
    if (el instanceof HTMLAnchorElement && !/^\/?#\/(overview|model|computation|calibration|simulation)(\/|$)/.test(el.getAttribute('href') || '')) throw new Error('External links are unavailable.');
    if (!el.matches('button,a,summary,[role="option"]')) throw new Error('Use the appropriate field operation.');
    el.click();
  } else if (action.operation === 'select' && el instanceof HTMLSelectElement) {
    if (!Array.from(el.options).some(o=>o.value===action.value && !o.disabled)) throw new Error('Choose an available option value.');
    el.value=String(action.value); el.dispatchEvent(new Event('change',{bubbles:true}));
  } else if (action.operation === 'check' && el instanceof HTMLInputElement && el.type === 'checkbox') {
    if (typeof action.value !== 'boolean') throw new Error('Checkbox value must be true or false.');
    if (el.checked !== action.value) el.click();
  } else if (action.operation === 'fill' && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
    if (el instanceof HTMLInputElement && !['text','search','number'].includes(el.type)) throw new Error('This input cannot be filled by the agent.');
    Object.getOwnPropertyDescriptor(el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,'value')!.set!.call(el,String(action.value));
    el.dispatchEvent(new Event('input',{bubbles:true})); el.dispatchEvent(new Event('change',{bubbles:true}));
  } else throw new Error('Operation does not match this control.');
  return {operated:label(el)};
}
